import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { Select } from 'antd';
import { format } from 'date-fns';
import { Building2, IndianRupee, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import { fetchExpenseStats } from '../utils/api';
import { formatIndianCurrency, formatIndianNumber } from '../utils/numberToWords';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function getFinancialYear(date) {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    const year = d.getFullYear();
    const month = d.getMonth();
    return month < 3 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  } catch {
    return null;
  }
}

function getCurrentFinancialYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  return month < 3 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
}

function getDateRangeForFYMonth(selectedFY, selectedMonth) {
  // Returns { startDate, endDate } in yyyy-MM-dd or nulls if not applicable
  if (!selectedFY) return { startDate: null, endDate: null };
  const [fyStartStr, fyEndStr] = selectedFY.split('-');
  const fyStartYear = parseInt(fyStartStr, 10);
  const fyEndYear = parseInt(fyEndStr, 10);

  // If no month filter, full FY range: Apr 1 to Mar 31
  if (selectedMonth === -1) {
    const start = new Date(fyStartYear, 3, 1); // Apr 1
    const end = new Date(fyEndYear, 2, 31, 23, 59, 59, 999); // Mar 31
    return { startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') };
  }

  // Month within FY: Apr-Dec use startYear; Jan-Mar use endYear
  const isQ1 = selectedMonth <= 2; // Jan-Feb-Mar
  const year = isQ1 ? fyEndYear : fyStartYear;
  const start = new Date(year, selectedMonth, 1);
  const end = new Date(year, selectedMonth + 1, 0, 23, 59, 59, 999);
  return { startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') };
}

export default function ProfitLoss({ invoices = [], facilities = [], clients = [] }) {
  // Since operational costs are from August 2025, use 2025-2026 as the current FY
  const currentFY = '2025-2026';
  const [selectedFY, setSelectedFY] = useState(currentFY);
  const [selectedMonth, setSelectedMonth] = useState(-1); // -1 = all months in FY
  const [facility, setFacility] = useState(''); // optional, applies to operational costs only
  const [expenseStats, setExpenseStats] = useState({ totalAmount: 0, categoryBreakdown: [], facilityBreakdown: [] });
  const [loading, setLoading] = useState(false);
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const chartInnerRef = useRef(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, label: '', revenue: 0, expenses: 0 });
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  // Inject print styles (once)
  const plPrintStyles = `
  @media print {
    @page { margin: 12mm; }
    .pl-report { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', Arial, sans-serif; padding-top: 38mm; padding-bottom: 18mm; }
    .pl-print-header { position: fixed; top: 0; left: 0; right: 0; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e5e7eb; padding: 8mm 0 4mm 0; }
    .pl-print-header .brand { display: flex; align-items: center; gap: 8px; }
    .pl-print-header img { height: 22mm; width: auto; }
    .pl-print-header .title { font-size: 18px; font-weight: 700; }
    .pl-print-header .meta { font-size: 12px; color: #6b7280; }
    .pl-print-footer { position: fixed; bottom: 0; left: 0; right: 0; text-align: center; border-top: 1px solid #e5e7eb; padding: 4mm 0 6mm 0; font-size: 12px; color: #6b7280; }
    .pl-print-footer .page:after { content: counter(page) ' / ' counter(pages); }
    /* Hide on-print in-content header summary */
    .pl-report .pl-header { display: none !important; }
    /* Fixed header/footer styles */
    .pl-report .pl-header-screen { margin: 6px 0 10px 0; padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 8px; }
    .pl-report .pl-row { display: flex; flex-wrap: wrap; gap: 14px; }
    .pl-report .pl-chip { background: #f3f4f6; padding: 6px 10px; border-radius: 9999px; font-size: 12px; }
    .pl-report .stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
    .pl-report .stat-card { border: 1px solid #e5e7eb; border-radius: 8px; }
    .pl-report .stat-card .stat-value { font-size: 18px; }
    .pl-report .table-container { break-inside: avoid; page-break-inside: avoid; }
    .pl-report .data-table { width: 100%; border-collapse: collapse; }
    .pl-report .data-table th, .pl-report .data-table td { border: 1px solid #9ca3af; padding: 6px 8px; font-size: 12px; }
    .pl-report .data-table thead th { background: #f3f4f6; }
    .pl-report h3 { margin: 6px 0; font-size: 14px; }
    .page-actions, .filters-section { display: none !important; }
  }
  `;

  if (typeof document !== 'undefined' && !document.getElementById('pl-print-style')) {
    const style = document.createElement('style');
    style.id = 'pl-print-style';
    style.innerHTML = plPrintStyles;
    document.head.appendChild(style);
  }

  // Available FYs from invoice data
  const allFYs = useMemo(() => {
    const set = new Set();
    invoices.forEach((inv) => {
      if (inv && inv.isCancelled) return;
      if (!inv.invoiceDate) return;
      const fy = getFinancialYear(inv.invoiceDate);
      if (fy) set.add(fy);
    });
    return Array.from(set).sort();
  }, [invoices]);

  // Map clientId -> facilityId for quick lookup
  const clientIdToFacilityId = useMemo(() => {
    const map = new Map();
    clients.forEach((c) => {
      const id = c._id || c.id;
      if (id) map.set(String(id), c.facilityId || '');
    });
    return map;
  }, [clients]);

  // Selected facility id (clients reference facilities by id)
  const selectedFacilityId = useMemo(() => {
    if (!facility) return '';
    const f = facilities.find((x) => x.name === facility);
    return f ? String(f.id) : '';
  }, [facility, facilities]);

  // Revenue from invoices based on FY/month and facility when selected
  const revenueTotal = useMemo(() => {
    const { startDate, endDate } = getDateRangeForFYMonth(selectedFY, selectedMonth);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const filtered = invoices.filter((inv) => {
      if (inv && inv.isCancelled) return false;
      if (!inv.invoiceDate) return false;
      const d = new Date(inv.invoiceDate);
      if (isNaN(d.getTime())) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      // Filter by facility if one is selected (match via client.facilityId)
      if (selectedFacilityId) {
        const clientId = inv.client && typeof inv.client === 'object' ? (inv.client._id || inv.client.id) : inv.client;
        const clientFacilityId = clientId ? clientIdToFacilityId.get(String(clientId)) : '';
        if (String(clientFacilityId || '') !== selectedFacilityId) return false;
      }
      return true;
    });
    return filtered.reduce((sum, inv) => sum + Number(inv.totals?.total || 0), 0);
  }, [invoices, selectedFY, selectedMonth, selectedFacilityId, clientIdToFacilityId]);

  // Convert month index to service month abbreviation
  const getServiceMonthFromIndex = (monthIndex) => {
    if (monthIndex === -1) return null; // All months
    const monthAbbrs = ['apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec', 'jan', 'feb', 'mar'];
    return monthAbbrs[monthIndex] || null;
  };

  // Load expense stats whenever filters change
  useEffect(() => {
    const { startDate, endDate } = getDateRangeForFYMonth(selectedFY, selectedMonth);
    
    // Use service month for more accurate P&L reporting
    const filters = { facility };
    const serviceMonth = getServiceMonthFromIndex(selectedMonth);
    if (serviceMonth) {
      filters.serviceMonth = serviceMonth;
    } else if (startDate && endDate) {
      // Fallback to date range if no specific month selected
      filters.startDate = startDate;
      filters.endDate = endDate;
    }
    
    const run = async () => {
      setLoading(true);
      try {
        const stats = await fetchExpenseStats(filters);
        setExpenseStats(stats || { totalAmount: 0, categoryBreakdown: [], facilityBreakdown: [] });
      } catch (err) {
        setExpenseStats({ totalAmount: 0, categoryBreakdown: [], facilityBreakdown: [] });
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [selectedFY, selectedMonth, facility]);

  // Build an array of months for the selected FY in Apr->Mar order
  const getFYMonths = (fyStr) => {
    if (!fyStr) return [];
    const [s, e] = fyStr.split('-').map((n) => parseInt(n, 10));
    const months = [];
    // Apr(3) to Dec(11) in start year
    for (let m = 3; m <= 11; m++) {
      const start = new Date(s, m, 1);
      const end = new Date(s, m + 1, 0, 23, 59, 59, 999);
      months.push({ key: m, label: format(start, 'MMM'), startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') });
    }
    // Jan(0) to Mar(2) in end year
    for (let m = 0; m <= 2; m++) {
      const start = new Date(e, m, 1);
      const end = new Date(e, m + 1, 0, 23, 59, 59, 999);
      months.push({ key: m, label: format(start, 'MMM'), startDate: format(start, 'yyyy-MM-dd'), endDate: format(end, 'yyyy-MM-dd') });
    }
    return months;
  };

  // When All Months is selected, compute monthly series for chart
  useEffect(() => {
    if (selectedMonth !== -1) {
      setMonthlyRevenue([]);
      setMonthlyExpenses([]);
      return;
    }
    const months = getFYMonths(selectedFY);
    // Revenue from invoices (client-side)
    const rev = months.map(({ startDate, endDate }) => {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const filtered = invoices.filter((inv) => {
        if (!inv.invoiceDate) return false;
        const d = new Date(inv.invoiceDate);
        if (isNaN(d.getTime())) return false;
        if (d < start || d > end) return false;
        if (selectedFacilityId) {
          const clientId = inv.client && typeof inv.client === 'object' ? (inv.client._id || inv.client.id) : inv.client;
          const clientFacilityId = clientId ? clientIdToFacilityId.get(String(clientId)) : '';
          if (String(clientFacilityId || '') !== selectedFacilityId) return false;
        }
        return true;
      });
      return filtered.reduce((sum, inv) => sum + Number(inv.totals?.total || 0), 0);
    });
    setMonthlyRevenue(rev);

    // Expenses from API per month - use service month for accuracy
    setMonthlyLoading(true);
    Promise.all(
      months.map(({ startDate, endDate }, index) => {
        const filters = { facility };
        const serviceMonth = getServiceMonthFromIndex(index);
        if (serviceMonth) {
          filters.serviceMonth = serviceMonth;
        } else {
          // Fallback to date range for "All Months" view
          filters.startDate = startDate;
          filters.endDate = endDate;
        }
        return fetchExpenseStats(filters).then((s) => Number(s?.totalAmount || 0)).catch(() => 0);
      })
    ).then((vals) => setMonthlyExpenses(vals)).finally(() => setMonthlyLoading(false));
  }, [selectedMonth, selectedFY, invoices, selectedFacilityId, clientIdToFacilityId, facility]);

  const totalExpenses = Number(expenseStats.totalAmount || 0);
  const profit = revenueTotal - totalExpenses;

  const renderValue = (val) => {
    if (val == null) return '-';
    if (typeof val === 'object') {
      // Common case for Mongo aggregation where _id can be an object
      if ('name' in val) return String(val.name);
      if ('_id' in val && typeof val._id === 'string') return val._id;
      try {
        return JSON.stringify(val);
      } catch {
        return String(val);
      }
    }
    return String(val);
  };

  const categoryRows = useMemo(() => {
    const rows = Array.isArray(expenseStats.categoryBreakdown) ? expenseStats.categoryBreakdown : [];
    return rows.map((row) => {
      const id = row && row._id;
      let nameStr = '';
      if (typeof id === 'object' && id !== null) {
        if (typeof id.name === 'string') nameStr = id.name;
        else if (typeof id._id === 'string') nameStr = id._id;
        else nameStr = JSON.stringify(id);
      } else {
        nameStr = String(id ?? '');
      }
      return { name: nameStr, total: Number(row?.total || 0), count: Number(row?.count || 0) };
    });
  }, [expenseStats.categoryBreakdown]);

  // Calculate revenue and operational costs for each facility when "All Facilities" is selected
  const facilityBreakdown = useMemo(() => {
    if (facility) return []; // Don't show breakdown when a specific facility is selected
    
    const { startDate, endDate } = getDateRangeForFYMonth(selectedFY, selectedMonth);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    // Get operational costs by facility from API response
    const facilityExpenses = Array.isArray(expenseStats.facilityBreakdown) ? expenseStats.facilityBreakdown : [];
    
    // Calculate revenue for each facility
    const facilityRevenue = new Map();
    invoices.forEach((inv) => {
      if (inv && inv.isCancelled) return;
      if (!inv.invoiceDate) return;
      const d = new Date(inv.invoiceDate);
      if (isNaN(d.getTime())) return;
      if (start && d < start) return;
      if (end && d > end) return;
      
      const clientId = inv.client && typeof inv.client === 'object' ? (inv.client._id || inv.client.id) : inv.client;
      const facilityId = clientId ? clientIdToFacilityId.get(String(clientId)) : '';
      if (facilityId) {
        const current = facilityRevenue.get(facilityId) || 0;
        facilityRevenue.set(facilityId, current + Number(inv.totals?.total || 0));
      }
    });

    // Combine revenue and operational costs for each facility
    return facilities.map((f) => {
      const facilityId = String(f.id);
      const revenue = facilityRevenue.get(facilityId) || 0;
      
      const expenseRow = facilityExpenses.find((e) => {
        const id = e && e._id;
        
        // The backend returns facility names as _id, not facility IDs
        if (typeof id === 'string') {
          return id === f.name; // Match by facility name
        }
        
        if (typeof id === 'object' && id !== null) {
          return String(id.name || id._id || '') === facilityId;
        }
        return String(id || '') === facilityId;
      });
      
      const operationalCosts = expenseRow ? Number(expenseRow.total || 0) : 0;
      const profit = revenue - operationalCosts;
      
      return {
        name: f.name,
        revenue,
        expenses: operationalCosts,
        profit,
        expenseCount: expenseRow ? Number(expenseRow.count || 0) : 0
      };
    }); // Show all facilities, even with zero activity
  }, [facilities, invoices, selectedFY, selectedMonth, facility, expenseStats.facilityBreakdown, clientIdToFacilityId]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title">
          <h1>Profit & Loss</h1>
          <p>View profit and loss calculated from invoices and operational costs</p>
        </div>
        {/* <div className="page-actions">
          <button className="btn btn-secondary" onClick={handlePrint}>Export PDF</button>
        </div> */}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <Filter size="1.2em" />
          <h3>Filters</h3>
        </div>
        <div className="filters-grid">
          <div className="filter-group">
            <label>Financial Year</label>
            <Select
              style={{ width: '100%' }}
              value={selectedFY}
              onChange={(v) => setSelectedFY(v)}
              options={(allFYs.length ? allFYs : [currentFY]).map(fy => ({ label: fy, value: fy }))}
              showSearch
              optionFilterProp="label"
            />
          </div>

          <div className="filter-group">
            <label>Month</label>
            <Select
              style={{ width: '100%' }}
              value={selectedMonth}
              onChange={(v) => setSelectedMonth(v)}
              options={[{ label: 'All Months', value: -1 }, ...MONTHS.map((m, idx) => ({ label: m, value: idx }))]}
            />
          </div>

          <div className="filter-group">
            <label>Facility (Operational Cost)</label>
            <Select
              style={{ width: '100%' }}
              value={facility || undefined}
              onChange={(v) => setFacility(v || '')}
              allowClear
              placeholder="All Facilities"
              options={facilities.map(f => ({ label: f.name, value: f.name }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
        </div>
      </div>

      {/* Printable block start */}
      <div ref={printRef} className="pl-report">
        {/* Print-only header/footer */}
        <div className="pl-print-header" style={{ display: 'none' }}>
          <div className="brand" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src="/spatium-logo-1.png" alt="Spatium" />
            <div>
              <div className="title">Profit & Loss Report</div>
              <div className="meta">FY {selectedFY} • {selectedMonth === -1 ? 'All Months' : MONTHS[selectedMonth]} • {facility || 'All Facilities'}</div>
            </div>
          </div>
          <div className="meta">Generated {format(new Date(), 'dd/MM/yyyy')}</div>
        </div>
        <div className="pl-print-footer" style={{ display: 'none' }}>
          <span>Page </span><span className="page"></span>
        </div>
        
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <IndianRupee size="2em" color="#4caf50" />
          </div>
          <div className="stat-content">
            <h3>Total Revenue</h3>
            <p className="stat-value">{formatIndianCurrency(revenueTotal)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Building2 size="2em" color="#ef5350" />
          </div>
          <div className="stat-content">
            <h3>Total Operational Cost</h3>
            <p className="stat-value">{loading ? 'Loading…' : formatIndianCurrency(totalExpenses)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            {profit >= 0 ? (
              <TrendingUp size="2em" color="#43a047" />
            ) : (
              <TrendingDown size="2em" color="#e53935" />
            )}
          </div>
          <div className="stat-content">
            <h3>Profit / Loss</h3>
            <p className="stat-value" style={{ color: profit >= 0 ? '#2e7d32' : '#c62828' }}>
              {formatIndianCurrency(profit)}
            </p>
          </div>
        </div>
      </div>

      {/* Monthly Revenue vs Expenses chart (only when All Months selected) */}
      {selectedMonth === -1 && (
        <div className="card-style" style={{ padding: 16, marginTop: 16 }}>
          <h3 style={{ margin: '0 0 12px 0' }}>Revenue vs Operational Cost by Month</h3>
          {monthlyLoading ? (
            <div className="loading">Loading chart…</div>
          ) : (
            <div>
              <div
                ref={chartInnerRef}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(12, 1fr)',
                  alignItems: 'end',
                  columnGap: 14,
                  height: 200,
                  position: 'relative',
                  paddingBottom: 8,
                  width: '100%'
                }}
              >
                {getFYMonths(selectedFY).map((m, idx) => {
                  const rev = monthlyRevenue[idx] || 0;
                  const exp = monthlyExpenses[idx] || 0;
                  const maxVal = Math.max(...monthlyRevenue, ...monthlyExpenses, 1);
                  const scale = (v) => Math.max(2, Math.round((v / maxVal) * 160));
                  return (
                    <div
                      key={idx}
                      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, width: '100%' }}
                      onMouseEnter={(e) => {
                        const rect = chartInnerRef.current?.getBoundingClientRect();
                        const x = e.clientX - (rect?.left || 0) + 12;
                        const y = e.clientY - (rect?.top || 0) - 24;
                        setTooltip({ visible: true, x, y, label: m.label, revenue: rev, expenses: exp });
                      }}
                      onMouseMove={(e) => {
                        const rect = chartInnerRef.current?.getBoundingClientRect();
                        const x = e.clientX - (rect?.left || 0) + 12;
                        const y = e.clientY - (rect?.top || 0) - 24;
                        setTooltip((t) => ({ ...t, x, y }));
                      }}
                      onMouseLeave={() => setTooltip((t) => ({ ...t, visible: false }))}
                    >
                      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 170, width: '70%', justifyContent: 'space-between' }}>
                        <div title={`Revenue: ${formatIndianCurrency(rev)}`} style={{ width: '45%', height: scale(rev), background: '#4caf50', borderRadius: 3 }}></div>
                        <div title={`Operational Cost: ${formatIndianCurrency(exp)}`} style={{ width: '45%', height: scale(exp), background: '#ef5350', borderRadius: 3 }}></div>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 12, color: '#555' }}>{m.label}</div>
                    </div>
                  );
                })}
                {tooltip.visible && (
                  <div
                    style={{
                      position: 'absolute',
                      left: tooltip.x,
                      top: tooltip.y,
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                      padding: '8px 10px',
                      fontSize: 12,
                      color: '#111827',
                      pointerEvents: 'none',
                      zIndex: 5,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltip.label}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 10, height: 10, background: '#4caf50', display: 'inline-block', borderRadius: 2 }}></span>
                      <span>Revenue: {formatIndianCurrency(tooltip.revenue)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                      <span style={{ width: 10, height: 10, background: '#ef5350', display: 'inline-block', borderRadius: 2 }}></span>
                      <span>Operational Cost: {formatIndianCurrency(tooltip.expenses)}</span>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16, marginTop: 8, color: '#555' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, background: '#4caf50', display: 'inline-block', borderRadius: 2 }}></span>
                  <span>Revenue</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 12, height: 12, background: '#ef5350', display: 'inline-block', borderRadius: 2 }}></span>
                  <span>Operational Cost</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Facility Breakdown - Only show when "All Facilities" is selected */}
      {!facility && facilityBreakdown.length > 0 && (
        <div className="table-container" style={{ marginTop: 16 }}>
          <div className="table-header">
            <h3>Profit & Loss by Facility ({formatIndianNumber(facilityBreakdown.length || 0, 0)} facilities)</h3>
          </div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Facility</th>
                  <th>Revenue</th>
                  <th>Operational Cost</th>
                  <th>Profit/Loss</th>
                  <th>Expense Count</th>
                </tr>
              </thead>
              <tbody>
                {facilityBreakdown.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.name || '-'}</td>
                    <td>{formatIndianCurrency(row.revenue)}</td>
                    <td>{formatIndianCurrency(row.expenses)}</td>
                    <td style={{ color: row.profit >= 0 ? '#2e7d32' : '#c62828', fontWeight: '500' }}>
                      {formatIndianCurrency(row.profit)}
                    </td>
                    <td>{formatIndianNumber(row.expenseCount, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expense Breakdown */}
      <div className="table-container" style={{ marginTop: 16 }}>
        <div className="table-header">
          <h3>Expense Breakdown by Category ({formatIndianNumber(categoryRows.length || 0, 0)} categories)</h3>
        </div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Amount</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {categoryRows.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.name || '-'}</td>
                  <td>{formatIndianCurrency(row.total)}</td>
                  <td>{formatIndianNumber(row.count, 0)}</td>
                </tr>
              ))}
              {categoryRows.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ textAlign: 'center', color: '#666' }}>No operational costs found for the selected period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    </div>
  );
}


